import Decimal, { type Numeric } from "decimal.js-light";

import { JobTaxVersion } from "@btnet/services-btnet/handlers/models/jobTaxVersion";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import { TaxMethod } from "@btnet/services-btnet/handlers/models/taxMethod";

import {
    type IRetainageRecalcResult,
    recalculateRetainageForAmountChange,
} from "utilities/lineItem/retainageUtility";
import { add, divide, multiply, round, subtract } from "utilities/math/math";
import { calculateTotalIncludingTax, calculateTotalWithTax } from "utilities/tax/tax.utilities";

import {
    type ICalculatedFields,
    type ICostLineItem,
    type IMarkupLineItem,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

/**
 * Re-derives the withheld $ off a freshly-computed `builderCost`, holding the row's retention
 * percent. Folded into the recalculation entry points below so an amount change carries its
 * retainage along instead of every caller re-deriving it. No-ops (`{}`) for rows without a
 * percent, which is every non-Bill entity.
 */
function reapplyRetainage(lineItem: ICostLineItem, builderCost: number): IRetainageRecalcResult {
    return recalculateRetainageForAmountChange({ ...lineItem, builderCost });
}

export function recalculateCostLineItemFields(
    lineItem: ICostLineItem,
): { builderCost: number } & IRetainageRecalcResult {
    const builderCost = round(multiply(lineItem.unitCost ?? 0, lineItem.quantity ?? 0), 2);
    return {
        builderCost,
        ...reapplyRetainage(lineItem, builderCost),
    };
}

// NOTE: All markup calculation functions in this file must be functionally equivalent to MarkupUtility.cs
// This method recalculates and sets markup fields. Be sure to use a spread operator when calling this method to preserve other existing line item data
export function recalculateAllFields(
    markupLineItem: IMarkupLineItem,
    lockPrice: boolean = false,
): ICalculatedFields & IRetainageRecalcResult {
    const calculated = calculateAllMarkupFields(markupLineItem, lockPrice);
    return {
        ...calculated,
        ...reapplyRetainage(markupLineItem, calculated.builderCost),
    };
}

function calculateAllMarkupFields(
    markupLineItem: IMarkupLineItem,
    lockPrice: boolean,
): ICalculatedFields {
    const markupType = markupLineItem.markupType;

    // When lockPrice is true, always recalculate from the locked owner price
    if (lockPrice) {
        const result = recalculateFieldsFromClientPrice(markupLineItem.ownerPrice, markupLineItem);
        result.markupType = markupType;
        return result;
    }

    switch (markupType) {
        case MarkupColumnTypes.Percentage:
            return recalculateFieldsFromMarkupPercent(markupLineItem.markupPercent, markupLineItem);
        case MarkupColumnTypes.Margin:
            let margin = markupLineItem.margin;
            if (
                (markupLineItem.margin === null || markupLineItem.margin === undefined) &&
                markupLineItem.markupPercent
            ) {
                margin = getMarginPercentageFromMarkupPercentage(markupLineItem.markupPercent);
            }
            return recalculateFieldsFromMargin(margin ?? 0, markupLineItem);
        case MarkupColumnTypes.Price:
            return recalculateFieldsFromMarkupAmount(markupLineItem.markupAmount, markupLineItem);
        case MarkupColumnTypes.UnitPrice:
            return recalculateFieldsFromMarkupPerUnit(markupLineItem.markupPerUnit, markupLineItem);
        case MarkupColumnTypes.OwnerPrice:
            return recalculateFieldsFromClientPrice(markupLineItem.ownerPrice, markupLineItem);
        default:
            return {
                markupType,
                markupPercent: 0,
                margin: 0,
                markupAmount: 0,
                markupPerUnit: 0,
                ownerPrice: 0,
                builderCost: 0,
                totalWithTax: 0,
            };
    }
}

function recalculateFieldsFromMarkupPercent(
    markupPercent: number,
    lineItem: ICostLineItem,
): ICalculatedFields {
    const { unitCost, quantity, totalEffectiveRate, taxMethod, effectiveTaxVersion } = lineItem;
    const builderCost = multiply(unitCost, quantity);
    const clientPrice = round(multiply(add(1, divide(markupPercent, 100)), builderCost), 2);
    return {
        markupType: MarkupColumnTypes.Percentage,
        markupPercent,
        markupAmount: round(divide(multiply(builderCost, markupPercent), 100), 2),
        markupPerUnit: round(multiply(unitCost ?? 0, divide(markupPercent, 100)), 2),
        margin: getMarginPercentageFromMarkupPercentage(markupPercent),
        ownerPrice: round(clientPrice, 2),
        builderCost: round(builderCost, 2),
        totalWithTax: recalculateTotalWithTax(
            clientPrice,
            totalEffectiveRate,
            taxMethod,
            effectiveTaxVersion,
        ),
    };
}

function recalculateFieldsFromMargin(margin: number, lineItem: ICostLineItem): ICalculatedFields {
    margin = Math.max(margin, 0); // Ensure margin is not negative
    margin = margin === 100 ? 0 : margin; // Prevent margin of 100% from being saved
    const { unitCost, quantity, totalEffectiveRate, taxMethod, effectiveTaxVersion } = lineItem;
    const builderCost = multiply(unitCost, quantity);
    const clientPrice = calculateClientPriceFromMargin(margin, builderCost);
    const markupAmount = subtract(clientPrice, builderCost);
    return {
        markupType: MarkupColumnTypes.Margin,
        markupPercent: getMarkupPercentageFromMarginPercentage(margin),
        markupAmount: round(markupAmount, 2),
        markupPerUnit: calculateMarkupPerUnit(markupAmount, quantity),
        margin: round(margin, 2),
        ownerPrice: round(clientPrice, 2),
        builderCost: round(builderCost, 2),
        totalWithTax: recalculateTotalWithTax(
            round(clientPrice, 2),
            totalEffectiveRate,
            taxMethod,
            effectiveTaxVersion,
        ),
    };
}

function recalculateFieldsFromMarkupAmount(
    markupAmount: number,
    lineItem: ICostLineItem,
): ICalculatedFields {
    const { unitCost, quantity, totalEffectiveRate, taxMethod, effectiveTaxVersion } = lineItem;
    const builderCost = multiply(unitCost, quantity);
    const clientPrice = add(builderCost, markupAmount);
    return {
        markupType: MarkupColumnTypes.Price,
        markupPercent: calculateMarkupPercent(markupAmount, builderCost),
        markupAmount: round(markupAmount, 2),
        markupPerUnit: calculateMarkupPerUnit(markupAmount, quantity),
        margin: calculateMarginFromMarkupAmount(markupAmount, clientPrice),
        ownerPrice: round(clientPrice, 2),
        builderCost: round(builderCost, 2),
        totalWithTax: recalculateTotalWithTax(
            round(clientPrice, 2),
            totalEffectiveRate,
            taxMethod,
            effectiveTaxVersion,
        ),
    };
}

function recalculateFieldsFromMarkupPerUnit(
    markupPerUnit: number,
    lineItem: ICostLineItem,
): ICalculatedFields {
    const { unitCost, quantity, totalEffectiveRate, taxMethod, effectiveTaxVersion } = lineItem;
    const builderCost = multiply(unitCost, quantity);
    const markupAmount = markupPerUnit * quantity;
    const clientPrice = add(builderCost, markupAmount);
    return {
        markupType: MarkupColumnTypes.UnitPrice,
        markupPercent: calculateMarkupPercent(markupAmount, builderCost),
        markupAmount: round(markupAmount, 2),
        markupPerUnit: round(markupPerUnit, 2),
        margin: calculateMarginFromMarkupAmount(markupAmount, clientPrice),
        ownerPrice: round(clientPrice, 2),
        builderCost: round(builderCost, 2),
        totalWithTax: recalculateTotalWithTax(
            round(clientPrice, 2),
            totalEffectiveRate,
            taxMethod,
            effectiveTaxVersion,
        ),
    };
}

function recalculateFieldsFromClientPrice(
    clientPrice: number,
    lineItem: ICostLineItem,
): ICalculatedFields {
    const { unitCost, quantity, totalEffectiveRate, taxMethod, effectiveTaxVersion } = lineItem;
    const builderCost = multiply(unitCost, quantity);
    const markupAmount = subtract(clientPrice, builderCost);
    return {
        markupType: MarkupColumnTypes.OwnerPrice,
        markupPercent: calculateMarkupPercent(markupAmount, builderCost),
        markupAmount: round(markupAmount, 2),
        markupPerUnit: calculateMarkupPerUnit(markupAmount, quantity),
        margin: calculateMarginFromMarkupAmount(markupAmount, clientPrice),
        ownerPrice: round(clientPrice, 2),
        builderCost: round(builderCost, 2),
        totalWithTax: recalculateTotalWithTax(
            clientPrice,
            totalEffectiveRate,
            taxMethod,
            effectiveTaxVersion,
        ),
    };
}

// Helpers
export function calculateClientPriceFromMargin(margin: number, builderCost: Decimal) {
    const marginDecimal = subtract(1, divide(margin, 100));
    if (marginDecimal && !marginDecimal.isZero()) {
        return divide(builderCost, marginDecimal);
    } else {
        // If margin is 100% (which it shouldn't be), return builder cost to avoid division by zero
        return builderCost;
    }
}

function calculateMarginFromMarkupAmount(markupAmount: Numeric, clientPrice: Numeric) {
    // If markup is negative, there is no margin, so return 0
    if (
        (markupAmount instanceof Decimal && markupAmount.isNegative()) ||
        (typeof markupAmount === "number" && markupAmount < 0) ||
        clientPrice === 0
    ) {
        return 0;
    }
    return round(multiply(divide(markupAmount, clientPrice), 100), 2);
}

function calculateMarkupPercent(markupAmount: number | Decimal, builderCost: Decimal) {
    if (builderCost && !builderCost.isZero()) {
        return round(multiply(divide(markupAmount, builderCost), 100), 2);
    } else {
        return 0;
    }
}

function calculateMarkupPerUnit(markupAmount: number | Decimal, quantity: number) {
    if (quantity && quantity !== 0) {
        return round(divide(markupAmount, quantity), 2);
    } else {
        return 0;
    }
}

function recalculateTotalWithTax(
    clientPrice: number,
    totalEffectiveRate: Numeric | undefined,
    taxMethod: TaxMethod | undefined,
    effectiveTaxVersion: JobTaxVersion | undefined,
): number {
    if (effectiveTaxVersion !== undefined && effectiveTaxVersion >= JobTaxVersion.V1) {
        return calculateTotalIncludingTax(clientPrice, totalEffectiveRate ?? 0);
    }

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    return calculateTotalWithTax(clientPrice, totalEffectiveRate ?? 0, taxMethod ?? TaxMethod.None);
}

export function getMarginPercentageFromMarkupPercentage(percentMarkup: number) {
    if (percentMarkup <= 0) {
        // builder is losing money, but we'll just say 0% margin
        return 0;
    }
    const percentMarkupAsDecimal = divide(percentMarkup, 100);
    return round(multiply(divide(percentMarkupAsDecimal, add(1, percentMarkupAsDecimal)), 100), 2);
}

export function getMarkupPercentageFromMarginPercentage(percentMargin: number) {
    const percentMarginAsDecimal = percentMargin / 100;
    if (percentMarginAsDecimal === 1) {
        // Margin should never be 100% but this will prevent an exception
        return 0;
    }
    return round((percentMarginAsDecimal / (1 - percentMarginAsDecimal)) * 100, 2);
}
